import { types, Instance, flow, getRoot } from 'mobx-state-tree';

import {
  getWorkouts as apiGetWorkouts,
  skipUserWorkout as apiSkipUserWorkout,
  flushWorkoutProgress as apiFlushWorkoutProgress,
} from '@/services/api/workoutApi';
import type { ApiWorkout } from '@/stores/workoutPlayer/types';
import { getWeekNumber, getWeekYear } from '@/services/datetime';

export type WorkoutMode = 'none' | 'card' | 'guided';

export type IWorkoutSessionStore = Instance<typeof WorkoutSessionStore>;

export function deriveModeFromApiWorkout(apiWorkout: ApiWorkout): WorkoutMode {
  const firstSegment = apiWorkout.segments?.[0];
  const hasChapters =
    !!firstSegment?.user_segment_chapters &&
    firstSegment.user_segment_chapters.length > 0;
  if (apiWorkout.segments?.length === 1 && hasChapters) {
    return 'guided';
  }
  return 'card';
}

const WorkoutSessionStore = types
  .model('WorkoutSessionStore', {
    mode: types.optional(
      types.enumeration('WorkoutMode', ['none', 'card', 'guided']),
      'none',
    ),
    templateType: types.maybeNull(types.string),
    pausedWorkoutIdsByTemplateType: types.map(types.number),
  })
  .volatile(() => ({
    pausedWorkoutDataByTemplateType: new Map<string, ApiWorkout>(),
  }))
  .views((self) => ({
    getPausedWorkoutId(templateType: string): number | null {
      return self.pausedWorkoutIdsByTemplateType.get(templateType) ?? null;
    },
    getPausedWorkoutData(templateType: string): ApiWorkout | null {
      return self.pausedWorkoutDataByTemplateType.get(templateType) ?? null;
    },
    hasPausedWorkout(templateType: string): boolean {
      return self.pausedWorkoutIdsByTemplateType.has(templateType);
    },
    get pausedTemplateTypes(): string[] {
      return Array.from(self.pausedWorkoutIdsByTemplateType.keys());
    },
  }))
  .actions((self) => ({
    setMode(mode: WorkoutMode) {
      self.mode = mode;
    },
    setTemplateType(type: string) {
      self.templateType = type;
    },
    clearPaused(templateType: string) {
      self.pausedWorkoutIdsByTemplateType.delete(templateType);
      self.pausedWorkoutDataByTemplateType.delete(templateType);
    },
    reset() {
      const root = getRoot(self) as any;

      // If we're tearing down while a workout is in progress (started but not
      // finished), flush partial progress so the backend records caloric
      // expenditure for what was done. Fire-and-forget — teardown is sync and
      // mustn't block. Idempotent on the backend.
      const activePlayer =
        self.mode === 'card'
          ? root.workoutPlayerStore
          : self.mode === 'guided'
            ? root.guidedWorkoutPlayerStore
            : null;
      const workoutId = activePlayer?.workout?.id;
      const startedAt = activePlayer?.workoutStartedAt;
      const completedAt = activePlayer?.workoutCompletedAt;
      if (workoutId && startedAt && !completedAt) {
        apiFlushWorkoutProgress(workoutId, startedAt);
      }

      root.workoutPlayerStore.reset();
      root.guidedWorkoutPlayerStore.reset();
      self.mode = 'none';
      self.templateType = null;
      // Note: paused workout entries are intentionally NOT cleared here —
      // they survive the in-memory session reset so home can rediscover them.
    },
    fetchPausedWorkouts: flow(function* () {
      try {
        const workouts: ApiWorkout[] = yield apiGetWorkouts(
          getWeekYear(),
          getWeekNumber(),
        );

        const newIds = new Map<string, number>();
        const newData = new Map<string, ApiWorkout>();

        for (const w of workouts) {
          const isUnfinished =
            w.started_at != null &&
            w.completed_at == null &&
            !(w as any).skipped_at;
          if (!isUnfinished) continue;
          const tt = w.workout_template_type;
          if (!tt) continue;

          // Most recent wins per template type: replace if id is higher.
          const existing = newIds.get(tt);
          if (existing == null || w.id > existing) {
            newIds.set(tt, w.id);
            newData.set(tt, w);
          }
        }

        // Replace the MST map atomically
        self.pausedWorkoutIdsByTemplateType.replace(
          Object.fromEntries(newIds.entries()),
        );
        self.pausedWorkoutDataByTemplateType = newData;
      } catch (err) {
        console.error('[Session] fetchPausedWorkouts failed:', err);
      }
    }),
    skipAndClear: flow(function* (templateType: string) {
      const id = self.pausedWorkoutIdsByTemplateType.get(templateType);
      if (id == null) return;
      yield apiSkipUserWorkout(id);
      self.pausedWorkoutIdsByTemplateType.delete(templateType);
      self.pausedWorkoutDataByTemplateType.delete(templateType);
    }),
  }));

export default WorkoutSessionStore;
